// ----------------------------------------------------------------------------
// Component: Page Header
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Swiper
import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, A11y, Keyboard } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';

// Snippets
import 'snippets/background-media/background-media';
import 'snippets/video-player/video-block';

// Styles
import './_hero.scss';
import './_hero--content-layout.scss';

// -------------------------------------
// Selectors
// -------------------------------------

var HERO_SELECTOR = '.page-hero--home';
var VIDEO_HERO_SELECTOR = '.page-hero--video';
var SWIPER_SELECTOR = '.page-hero__swiper';
var SLIDER_NAV_SELECTOR = '.page-hero__slider-nav';
var PREV_BTN_SELECTOR = '.page-hero__slider-btn--prev';
var NEXT_BTN_SELECTOR = '.page-hero__slider-btn--next';
var PAGINATION_SELECTOR = '.page-hero__pagination';
var MUTE_BTN_SELECTOR = '.page-hero__control--mute';
var UNMUTE_BTN_SELECTOR = '.page-hero__control--unmute';
var PLAYPAUSE_BTN_SELECTOR = '.page-hero__control--playpause';
var VIDEO_BLOCK_SELECTOR = '.video-block';

// -------------------------------------
// CSS State Classes
// (applied to the .page-hero--home element)
// -------------------------------------

var CLASS_HAS_VIDEO = 'hero--has-video';
var CLASS_IS_MUTED = 'hero--is-muted';
var CLASS_IS_PAUSED = 'hero--is-paused';

// -------------------------------------
// Helpers
// -------------------------------------

function getActiveSlide(swiper) {
  return swiper.slides[swiper.activeIndex] || null;
}
function getSlideVideo(slide) {
  return slide ? slide.querySelector('video') : null;
}
function getPaginationBullets(swiper) {
  if (!swiper.pagination || !swiper.pagination.bullets) return [];
  return Array.from(swiper.pagination.bullets);
}
function slideHasVideo(slide) {
  if (!slide) return false;
  if (slide.dataset.hasVideo != null) {
    return slide.dataset.hasVideo === 'true';
  }
  var inner = slide.querySelector('[data-has-video]');
  return inner ? inner.dataset.hasVideo === 'true' : false;
}
function dispatchSlideVideoEvent(slide, eventName) {
  var videoBlock = slide ? slide.querySelector(VIDEO_BLOCK_SELECTOR) : null;
  if (!videoBlock) return false;
  videoBlock.dispatchEvent(new Event(eventName));
  return true;
}
function pauseSlideVideo(slide) {
  var video = getSlideVideo(slide);
  if (!video) return;
  dispatchSlideVideoEvent(slide, 'pause.video-block');
  if (!video.paused) video.pause();
}
function playSlideVideo(slide, muted) {
  var video = getSlideVideo(slide);
  if (!video) return;
  video.muted = muted;
  dispatchSlideVideoEvent(slide, 'play.video-block');
  video.play().catch(() => {
    // Autoplay blocked — leave in paused state
  });
}
function setPlayPauseButtonState(button, paused) {
  if (!button) return;
  button.setAttribute('aria-pressed', paused ? 'true' : 'false');
  var label = paused ? button.dataset.labelPlay : button.dataset.labelPause;
  if (label) {
    button.setAttribute('aria-label', label);
  }
}
function setHeroPausedState(hero, button, paused) {
  hero.classList.toggle(CLASS_IS_PAUSED, paused);
  setPlayPauseButtonState(button, paused);
}
function resetPaginationIndicatorProgress(swiper) {
  getPaginationBullets(swiper).forEach(bullet => {
    bullet.style.removeProperty('--hero-bullet-progress');
  });
}
function setActivePaginationIndicatorProgress(swiper, progress) {
  var bullets = getPaginationBullets(swiper);
  var activeBullet = bullets[swiper.activeIndex];
  if (!activeBullet) return;
  var clampedProgress = Math.max(0, Math.min(progress, 1));
  activeBullet.style.setProperty('--hero-bullet-progress', "".concat(clampedProgress));
}
function syncPaginationIndicatorProgress(swiper) {
  var progress = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  resetPaginationIndicatorProgress(swiper);
  setActivePaginationIndicatorProgress(swiper, progress);
}
function updateVideoControls(hero, swiper) {
  var activeSlide = getActiveSlide(swiper);
  if (slideHasVideo(activeSlide)) {
    hero.classList.add(CLASS_HAS_VIDEO);
  } else {
    hero.classList.remove(CLASS_HAS_VIDEO);
  }
}

// -------------------------------------
// Hero Slider Init
// -------------------------------------

function initHeroSlider(hero) {
  var swiperEl = hero.querySelector(SWIPER_SELECTOR);
  if (!swiperEl) return null;
  var prevBtn = hero.querySelector(PREV_BTN_SELECTOR);
  var nextBtn = hero.querySelector(NEXT_BTN_SELECTOR);
  var paginationEl = hero.querySelector(PAGINATION_SELECTOR);
  var sliderNav = hero.querySelector(SLIDER_NAV_SELECTOR);
  var muteBtn = hero.querySelector(MUTE_BTN_SELECTOR);
  var unmuteBtn = hero.querySelector(UNMUTE_BTN_SELECTOR);
  var playpauseBtn = hero.querySelector(PLAYPAUSE_BTN_SELECTOR);

  // Read autoplay settings from data attributes set by Liquid
  var autoplayEnabled = hero.dataset.autoplay !== 'false';
  var autoplayDelay = (parseInt(hero.dataset.autoplaySpeed, 10) || 5) * 1000;
  if (!autoplayEnabled) {
    hero.classList.add('hero--autoplay-disabled');
  }

  // Videos start muted (browser autoplay policy)
  var isMuted = true;
  var isPaused = !autoplayEnabled;

  // Reflect initial muted state so CSS shows the unmute button
  hero.classList.add(CLASS_IS_MUTED);
  setHeroPausedState(hero, playpauseBtn, isPaused);
  var swiper = new Swiper(swiperEl, {
    modules: [Navigation, Pagination, Autoplay, A11y, Keyboard],
    slidesPerView: 1,
    loop: false,
    // Wrap navigation at the ends: tapping "next" on the last slide returns
    // to the first slide, and "prev" on the first slide jumps to the last.
    // rewind (vs loop) avoids cloning slides, so activeIndex === realIndex
    // and the existing video/pagination index logic stays correct.
    rewind: true,
    watchOverflow: true,
    autoplay: autoplayEnabled ? {
      delay: autoplayDelay,
      disableOnInteraction: false
    } : false,
    navigation: {
      prevEl: prevBtn,
      nextEl: nextBtn
    },
    pagination: {
      el: paginationEl,
      type: 'bullets',
      clickable: true
    },
    a11y: {
      prevSlideMessage: 'Previous slide',
      nextSlideMessage: 'Next slide'
    },
    keyboard: {
      enabled: true
    },
    on: {
      init(s) {
        // Hide slider nav when there is only one slide
        if (s.slides.length <= 1 && sliderNav) {
          sliderNav.hidden = true;
        }
        syncPaginationIndicatorProgress(s, autoplayEnabled ? 0 : 1);
        var firstSlide = getActiveSlide(s);
        if (isPaused) {
          pauseSlideVideo(firstSlide);
        } else {
          playSlideVideo(firstSlide, isMuted);
        }
        updateVideoControls(hero, s);
      },
      slideChangeTransitionStart(s) {
        // Pause the outgoing slide's video
        var prevSlide = s.slides[s.previousIndex] || null;
        pauseSlideVideo(prevSlide);
      },
      slideChangeTransitionEnd(s) {
        syncPaginationIndicatorProgress(s, autoplayEnabled ? 0 : 1);
        var activeSlide = getActiveSlide(s);
        if (isPaused) {
          pauseSlideVideo(activeSlide);
        } else {
          playSlideVideo(activeSlide, isMuted);
        }
        updateVideoControls(hero, s);
      },
      // Swiper may try to resume autoplay after manual navigation; keep
      // the hero paused until the user explicitly presses play again.
      autoplayResume(s) {
        if (!autoplayEnabled || !isPaused) return;
        if (typeof s.autoplay.pause === 'function') {
          s.autoplay.pause();
        } else {
          s.autoplay.stop();
        }
      },
      autoplayTimeLeft(s, timeLeft, percentage) {
        if (!autoplayEnabled || isPaused || typeof timeLeft !== 'number') {
          return;
        }
        setActivePaginationIndicatorProgress(s, 1 - percentage);
      }
    }
  });
  var pauseAutoplay = () => {
    if (!autoplayEnabled) return;
    if (typeof swiper.autoplay.pause === 'function' && swiper.autoplay.running) {
      swiper.autoplay.pause();
      return;
    }
    if (swiper.autoplay.running) {
      swiper.autoplay.stop();
    }
  };
  var resumeAutoplay = () => {
    if (!autoplayEnabled) return;
    if (typeof swiper.autoplay.resume === 'function' && swiper.autoplay.running) {
      swiper.autoplay.resume();
      return;
    }
    swiper.autoplay.start();
  };

  // -------------------------------------
  // Mute / Unmute
  // -------------------------------------

  if (muteBtn) {
    muteBtn.addEventListener('click', () => {
      isMuted = true;
      hero.classList.add(CLASS_IS_MUTED);
      var video = getSlideVideo(getActiveSlide(swiper));
      if (video) video.muted = true;
    });
  }
  if (unmuteBtn) {
    unmuteBtn.addEventListener('click', () => {
      isMuted = false;
      hero.classList.remove(CLASS_IS_MUTED);
      var video = getSlideVideo(getActiveSlide(swiper));
      if (video) video.muted = false;
    });
  }

  // -------------------------------------
  // Play / Pause toggle
  // -------------------------------------

  if (playpauseBtn) {
    playpauseBtn.addEventListener('click', () => {
      var activeSlide = getActiveSlide(swiper);
      if (isPaused) {
        isPaused = false;
        setHeroPausedState(hero, playpauseBtn, false);
        resumeAutoplay();
        playSlideVideo(activeSlide, isMuted);
      } else {
        isPaused = true;
        setHeroPausedState(hero, playpauseBtn, true);
        pauseAutoplay();
        pauseSlideVideo(activeSlide);
      }
    });
  }
  return swiper;
}
function initVideoHeroControls(hero) {
  var video = hero.querySelector('video');
  var muteBtn = hero.querySelector(MUTE_BTN_SELECTOR);
  var unmuteBtn = hero.querySelector(UNMUTE_BTN_SELECTOR);
  var playpauseBtn = hero.querySelector(PLAYPAUSE_BTN_SELECTOR);
  if (!video || !playpauseBtn) return null;
  var onMuteClick = () => {
    video.muted = true;
    hero.classList.add(CLASS_IS_MUTED);
  };
  var onUnmuteClick = () => {
    video.muted = false;
    hero.classList.remove(CLASS_IS_MUTED);
  };
  var syncPlaybackState = () => {
    if (video.paused) {
      hero.classList.add(CLASS_IS_PAUSED);
      playpauseBtn.setAttribute('aria-label', 'Play video');
      playpauseBtn.setAttribute('aria-pressed', 'true');
    } else {
      hero.classList.remove(CLASS_IS_PAUSED);
      playpauseBtn.setAttribute('aria-label', 'Pause video');
      playpauseBtn.setAttribute('aria-pressed', 'false');
    }
  };
  var onPlayPauseClick = () => {
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };
  if (video.muted) {
    hero.classList.add(CLASS_IS_MUTED);
  } else {
    hero.classList.remove(CLASS_IS_MUTED);
  }
  syncPlaybackState();
  if (muteBtn) muteBtn.addEventListener('click', onMuteClick);
  if (unmuteBtn) unmuteBtn.addEventListener('click', onUnmuteClick);
  playpauseBtn.addEventListener('click', onPlayPauseClick);
  video.addEventListener('play', syncPlaybackState);
  video.addEventListener('pause', syncPlaybackState);
  return () => {
    if (muteBtn) muteBtn.removeEventListener('click', onMuteClick);
    if (unmuteBtn) unmuteBtn.removeEventListener('click', onUnmuteClick);
    playpauseBtn.removeEventListener('click', onPlayPauseClick);
    video.removeEventListener('play', syncPlaybackState);
    video.removeEventListener('pause', syncPlaybackState);
  };
}

// -------------------------------------
// Mount / Unmount
// -------------------------------------

var heroInstances = new Map();
function mountHeroes() {
  heroInstances.forEach(instance => {
    if (instance !== null && instance !== void 0 && instance.destroy) {
      instance.destroy(true, true);
      return;
    }
    if (typeof instance === 'function') {
      instance();
    }
  });
  heroInstances.clear();
  document.querySelectorAll(HERO_SELECTOR).forEach(hero => {
    var swiper = initHeroSlider(hero);
    if (swiper) heroInstances.set(hero, swiper);
  });
  document.querySelectorAll(VIDEO_HERO_SELECTOR).forEach(hero => {
    var cleanup = initVideoHeroControls(hero);
    if (cleanup) heroInstances.set(hero, cleanup);
  });
}

// -------------------------------------
// Hooks
// -------------------------------------

mountHeroes();
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector(HERO_SELECTOR)) {
    mountHeroes();
  }
});
document.addEventListener('shopify:section:unload', event => {
  [HERO_SELECTOR, VIDEO_HERO_SELECTOR].forEach(selector => {
    var hero = event.target.querySelector(selector);
    if (!hero || !heroInstances.has(hero)) return;
    var instance = heroInstances.get(hero);
    if (instance !== null && instance !== void 0 && instance.destroy) {
      instance.destroy(true, true);
    } else if (typeof instance === 'function') {
      instance();
    }
    heroInstances.delete(hero);
  });
});